"use client"

import type { SelectedCcFilters, SelectedTagsFilter } from "@/modules/jobAds/client/hooks/useCcFilters"
import type { SelectedSecondaryFilters } from "@/modules/jobAds/client/hooks/useSecondaryFilters"
import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useState } from "react"

import { Filter } from "@jobteaser/spark/components/Icons/Filter"

import { useCcFilters } from "@/modules/jobAds/client/hooks/useCcFilters"
import { useJobAdsFilterNavigation } from "@/modules/jobAds/client/hooks/useJobAdsFilterNavigation"
import { useSecondaryFilters } from "@/modules/jobAds/client/hooks/useSecondaryFilters"
import { FilterButton } from "@/modules/landingSearch/components/FilterButton/FilterButton"

import styles from "./SecondaryFilters.module.css"

type SecondaryFiltersProps = {
  isJobsWithImpactFilterVisible: boolean
  label: string
}

const DynamicSecondaryFiltersModal = dynamic(() =>
  import("@/modules/jobAds/client/components/SecondaryFiltersModal/SecondaryFiltersModal").then(
    mod => mod.SecondaryFiltersModal
  )
)

export const SecondaryFilters: FunctionComponent<SecondaryFiltersProps> = ({
  isJobsWithImpactFilterVisible,
  label,
}) => {
  const { navigateWithUpdatedQueryParams } = useJobAdsFilterNavigation()
  const { selectedSecondaryFiltersNumber, getSecondaryParameters } = useSecondaryFilters()
  const { selectedCcFiltersNumber, getCcParameters, getTagParameter } = useCcFilters()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleApplyFilters = (
    secondaryFilters: SelectedSecondaryFilters,
    newCcFilters: SelectedCcFilters,
    newTagsFilter: SelectedTagsFilter
  ): void => {
    setIsModalOpen(false)
    navigateWithUpdatedQueryParams({
      availableFilters: {
        ...getCcParameters(newCcFilters),
        ...getSecondaryParameters(secondaryFilters),
        ...getTagParameter(newTagsFilter),
      },
    })
  }

  const handleCloseModal = (): void => setIsModalOpen(false)

  const filtersNumber = selectedCcFiltersNumber + selectedSecondaryFiltersNumber
  const isButtonFilled = filtersNumber > 0

  return (
    <div className={styles.main}>
      <div className={styles.actions}>
        <FilterButton
          className={styles.button}
          counter={isButtonFilled ? filtersNumber : null}
          data-testid="job-ads-secondary-filters-modal-open-button"
          icon={Filter}
          isActive={isButtonFilled}
          onClick={() => setIsModalOpen(true)}
        >
          {label}
        </FilterButton>
      </div>

      {isModalOpen && (
        <DynamicSecondaryFiltersModal
          isJobsWithImpactFilterVisible={isJobsWithImpactFilterVisible}
          onApply={handleApplyFilters}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}
